<script lang="ts" setup>
import type {
  DisplayableOfferProperty,
  OfferImage,
  OfferMedium,
  OfferSmall,
  MulticardPreviewItem,
} from '~/types/Catalog';
import { setUrlHash } from '~/utils/feedbackUrl';

const { offer, recommendationsBlockId, displayNameMode = 'default' } = defineProps<{
  offer: OfferMedium;
  recommendationsBlockId?: string;
  displayNameMode?: 'default' | 'without-properties';
}>();
const emit = defineEmits<{ selectOffer: [offer: OfferMedium] }>();
const catalogStore = useCatalogStore();
const selectedOffer = ref(offer);
const initialMulticardPreview = offer.multicard_preview;
const selectedMulticardPreviewItem = ref<MulticardPreviewItem | null>(null);
const isPreviewSelectionLoading = ref(false);
let previewSelectionRevision = 0;

const images = computed<OfferImage[]>(() =>
  [...selectedOffer.value.offer_images, ...selectedOffer.value.product_images]
    .sort((a, b) => Number(b.is_main) - Number(a.is_main))
    .slice(0, 6),
);

async function goToReviews() {
  await navigateTo(setUrlHash(selectedOffer.value.uri, 'reviews'));
}

function getPreviewItemOfferId(uri: string): number | null {
  const offerId = new URL(uri, 'https://wtfish.ru').searchParams.get('offer_id');
  const parsedOfferId = Number(offerId);

  return Number.isInteger(parsedOfferId) && parsedOfferId > 0 ? parsedOfferId : null;
}

async function selectMulticardPreviewItem(item: MulticardPreviewItem) {
  const offerId = getPreviewItemOfferId(item.uri);

  if (!offerId || offerId === selectedOffer.value.id || isPreviewSelectionLoading.value) return;

  const selectionRevision = ++previewSelectionRevision;
  isPreviewSelectionLoading.value = true;
  const [offer] = await catalogStore.getOffersList(String(offerId));

  if (selectionRevision === previewSelectionRevision && offer) {
    selectedOffer.value = offer;
    selectedMulticardPreviewItem.value = item;
    emit('selectOffer', offer);
  }

  if (selectionRevision === previewSelectionRevision) isPreviewSelectionLoading.value = false;
}

function getOfferColor(offer: OfferSmall | OfferMedium): string {
  return offer.displayable_offer_properties.find((p) => p.name === 'Цвет')?.value ?? '';
}

function sortSizeOffers(offer: OfferMedium) {
  const { size_charts_values, offers, size_charts } = offer;

  if (!size_charts_values || size_charts_values.length === 0) return offers;

  const sizeChartId = size_charts_values[0].size_chart_id;
  const chart = size_charts.find((sizeChart) => sizeChart.id === sizeChartId);
  const sizeChartValues = chart ? chart.size_chart_values : [];

  return offers
    .map((offer) => {
      const sizeProp = offer.displayable_offer_properties?.find((p: DisplayableOfferProperty) =>
        p.name.includes('Размер'),
      );

      return {
        ...offer,
        order:
          sizeChartValues.find((sizeChartValue) => {
            return offer.sizes?.[0].value === sizeChartValue.title || sizeProp?.value === sizeChartValue.title;
          })?.order ?? undefined,
        $isDisabled: offer.balance !== undefined && offer.balance < 1,
      };
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

const sizes = computed(() => {
  if (selectedOffer.value.multicard_preview?.enabled) {
    return '';
  }

  const currentOfferColor = getOfferColor(selectedOffer.value);

  if (currentOfferColor) {
    const sortedSizeOffers = sortSizeOffers(selectedOffer.value);

    return sortedSizeOffers
      .filter((offer) => getOfferColor(offer) === currentOfferColor)
      .map((o) => {
        const sizeProp = o.displayable_offer_properties.find((p) => p.name === 'Размер');
        return sizeProp?.value;
      })
      .join(' ');
  }
  return '';
});

const offerDisplayName = computed(() => {
  if (displayNameMode === 'without-properties') {
    return selectedOffer.value.product_name_without_properties?.trim() || getOfferDisplayName(selectedOffer.value);
  }

  return getOfferDisplayName(selectedOffer.value);
});
const multicardPreview = computed(() => initialMulticardPreview);
const { isMobile } = useBreakpoints();
const multicardPreviewItems = computed(() => {
  const preview = multicardPreview.value;
  const items = isMobile.value && preview?.mobile_items ? preview.mobile_items : (preview?.items ?? []);
  const selectedItem = selectedMulticardPreviewItem.value ?? {
    has_balance: true,
    id: selectedOffer.value.id,
    label:
      selectedOffer.value.displayable_offer_properties.find((property) => property.name === preview?.property_name)
        ?.value ?? selectedOffer.value.product_name_with_offer_properties,
    thumbnail_url:
      selectedOffer.value.offer_images[0]?.conversions_webp.thumb_url_180 ??
      selectedOffer.value.offer_images[0]?.url ??
      null,
    uri: selectedOffer.value.uri,
  };

  if (
    items.some(
      (item) => getPreviewItemOfferId(item.uri) === selectedOffer.value.id || item.uri === selectedOffer.value.uri,
    )
  ) {
    return items;
  }

  return [...items.slice(0, Math.max(0, items.length - 1)), selectedItem];
});
const multicardPreviewRemaining = computed(() => {
  const preview = multicardPreview.value;

  if (isMobile.value && preview?.mobile_items) {
    return preview.mobile_more_count ?? Math.max(0, preview.total - preview.mobile_items.length);
  }

  return preview?.more_count ?? preview?.remaining ?? Math.max(0, (preview?.total ?? 0) - (preview?.items.length ?? 0));
});
const selectedMulticardPreviewOfferId = computed(() => selectedOffer.value.id);
const { accessRights } = storeToRefs(useAuthStore());

watch(
  () => offer,
  (offer) => {
    selectedOffer.value = offer;
  },
);
</script>

<template>
  <CardBlock class="product-card">
    <div class="product-card__image">
      <ProductCardImage
        :alt="offerDisplayName"
        :images
        class="pt-5"
      />

      <BadgesAndFlagsBlock
        :offer="selectedOffer"
        class="product-card__badges-block"
        used-in="category"
      />

      <FavoriteBtn
        :offer="selectedOffer"
        class="product-card__favorite-btn"
      />
    </div>

    <section class="product-card__info">
      <div class="flex justify-between flex-wrap text-sm">
        <AvailabilityIndicator :offer="selectedOffer" />

        <button
          v-if="Number(selectedOffer.rating) > 0"
          class="flex items-center gap-1 transition hover:underline"
          @click.stop.prevent="goToReviews"
        >
          <SvgoStar
            class="text-black text-xs"
            filled
          />
          {{ Number(selectedOffer.rating).toFixed(1) }}
        </button>
      </div>

      <div class="product-card__content flex-grow transition leading-5">
        <span class="product-card__title">{{ offerDisplayName }}</span>

        <div
          v-if="sizes"
          class="product-card__sizes"
        >
          {{ sizes }}
        </div>

        <div
          v-else-if="multicardPreview?.enabled && multicardPreviewItems.length"
          :class="{ 'product-card__multicard-preview--text': multicardPreview.format === 'text' }"
          class="product-card__multicard-preview"
        >
          <button
            v-for="item in multicardPreviewItems"
            :key="item.id"
            :class="{
              'product-card__multicard-preview-item--muted': !item.has_balance,
              'product-card__multicard-preview-item--selected':
                getPreviewItemOfferId(item.uri) === selectedMulticardPreviewOfferId || item.uri === selectedOffer.uri,
            }"
            class="product-card__multicard-preview-item"
            type="button"
            @click.stop.prevent="selectMulticardPreviewItem(item)"
          >
            <span
              v-if="multicardPreview.format !== 'text' && item.thumbnail_url"
              class="product-card__multicard-preview-image"
            >
              <img
                :src="item.thumbnail_url"
                alt=""
              />
            </span>

            <span
              v-else
              class="product-card__multicard-preview-label"
            >
              {{ item.label }}
            </span>
          </button>

          <span
            v-if="multicardPreviewRemaining > 0"
            class="product-card__multicard-preview-more"
          >
            +{{ multicardPreviewRemaining }}
          </span>
        </div>
      </div>

      <ProductCardFooter
        :key="selectedOffer.id"
        :offer="selectedOffer"
        :recommendations-block-id="recommendationsBlockId"
      />

      <AdminOfferAvailabilityBlock :offer="selectedOffer" />
    </section>
  </CardBlock>
</template>

<style lang="scss" scoped>
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow: hidden;
  height: 100%;
  border: 1px solid transparent;
  font-size: $text-sm;
  transition: border-color 0.3s;

  @include screen-sm {
    gap: 16px;
  }

  &:hover {
    border-color: $black-10;

    a {
      color: $black;
    }

    @include screen-lg {
      .product-card-footer {
        border-color: $black;
        background-color: $black;
        color: $white;
      }
    }
  }

  &__image {
    position: relative;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    font-size: $text-sm;

    @include screen-md {
      font-size: $text-base;
    }
  }

  &__sizes {
    overflow: hidden;
    margin-top: auto;
    padding-top: 4px;
    color: $black-40;
    font-size: $text-xs;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__content {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &__title {
    height: auto;
  }

  &__multicard-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: auto;
    padding-top: 8px;

    &--text {
      width: 100%;
    }
  }

  &__multicard-preview-item,
  &__multicard-preview-more {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    max-width: 76px;
    height: 28px;
    min-height: 28px;
    padding: 2px 6px;
    border: 1px solid $black-20;
    border-radius: 6px;
    background-color: $white;
    color: $black-60;
    font-size: $text-xs;
    line-height: 1.15;
  }

  &__multicard-preview-item {
    transition:
      border-color 0.2s,
      color 0.2s;

    &:hover {
      border-color: $black-60;
      color: $black;
    }

    &--muted {
      border-color: $black-10;
      color: $black-40;
    }

    &--selected,
    &--selected:hover {
      border-color: #e84339;
      color: $black;
    }
  }

  &__multicard-preview--text &__multicard-preview-item {
    max-width: calc(50% - 2px);
  }

  &__multicard-preview-more {
    border-color: $black-20;
    background-color: $black-20;
  }

  &__multicard-preview-image {
    overflow: hidden;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    background-color: $black-5;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
      padding: 1px;
      transition: transform 0.2s;
    }
  }

  &__multicard-preview-item:hover &__multicard-preview-image img {
    transform: scale(1.08);
  }

  &__multicard-preview-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__badges-block {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 11;
  }

  &__favorite-btn {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 11;
  }
}
</style>
